import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Typography, Result, Table, Avatar, Tag, Tabs, Space } from "antd";
import { PhoneOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import csvUtil from "../../../utils/csv";

//
import useAuthStore from "../../../states/useAuth";

//
import DelaySearch from "../../../components/Table/DelaySearch";
import ActionHeader from "../../../components/Table/ActionHeader";
import MainTabs from "../../../components/MerchantUser/Report/MainTabs";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import useReport from "../../../hooks/useQuery/useReport";
import TableColumns from "../../../components/MerchantUser/Report/TableColumns";
import { isSuper } from "../../../utils/permission";
import { formatTime } from "../../../utils/time";

const { Title, Text, Paragraph } = Typography;

const csvFileName = "transfer_record";

const Transfer = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  //
  const { user, auth } = useAuthStore();
  const shopIds = user?.permission?.report;

  //
  const logsQueryKey = useMemo(() => ["/report/transfer", shopIds], [shopIds]);
  const requestData = useMemo(() => {
    const data = {};
    // Conditional payload construction
    if (shopIds[0] === "all") {
      data.companyId = user.companyId;
    } else {
      data["shop.id"] = { $in: shopIds };
    }
    return data;
  }, [shopIds, user.companyId]); // Include user.companyId if needed

  const {
    data: logs = [], // Use standard data object name
    isFetching: isFetchingLogs,
    error: logsError,
    refetch,
  } = useReport(
    !!shopIds, // Enable fetching only if shopIds exist
    logsQueryKey, // Unique query key
    "reports/transfer/query", // URL for the API request
    requestData // Request payload
  );

  //Table
  const [collapsed, setCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(logs.length);
  const pageRef = useRef();

  const filterData = (searchValue, data) => {
    if (!searchValue) return data;

    const lowerSearchValue = searchValue.toLowerCase();

    return data.filter((record) => {
      const id = record?.id?.toLowerCase() || "";
      return id.includes(lowerSearchValue);
    });
  };

  const filteredData = useMemo(() => {
    return logs;
  }, [logs, searchValue]);

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const translations = {
    recordIdTranslation: t("DCBKrecord.recordId"),
    timeTranslation: t("DCbuy.Time"),
    statusTranslation: t("DCbuy.status"),
    assistantTranslation: t("DCbuy.assistant"),
    consumerTranslation: t("DCbuy.consumer"),
    totalAmountTranslation: t("DCbuy.totalAmount"),
  };

  const { columns } = TableColumns({
    columnOrder: ["id", "time", "from", "to", "txTotalAmount", "txTotalQty"],
    translations,
  });

  const expandedRowRender = (record) => {
    // Define the order of columns you want for the expanded table
    const { columns: itemColumns } = TableColumns({
      columnOrder: record?.items?.[0].giftCard
        ? ["product", "productType", "qty", "balance"]
        : ["product", "productType", "value", "qty", "totalAmount"],
      translations,
    });

    return (
      <Table
        style={{
          tableLayout: "fixed",
          width: "60%",
        }}
        columns={itemColumns}
        dataSource={record.items}
        pagination={false}
        rowKey="id"
      />
    );
  };

  const getCSVRowData = (record) => {
    const rows = [];
    for (let i = 0; i < record.items.length; i++) {
      const item = record.items[i];

      rows.push({
        [t("DCbuy.Time")]: formatTime(record.time, "YYYY-MM-DD HH:mm:ss"),
        [t("DCBKrecord.recordId")]: record.id || "--",
        ["Product Type"]: item?.giftCard ? "GiftCard" : "Voucher",
        ["Product Name"]: item.name || "--",
        ["Product ID"]: item.id || "--",
        ["GiftCard ID"]: item?.giftCard ? item.giftCard.id : "--",
        [t("DCbuy.qty")]: item.qty || "--",
        ["Remaining Balance"]: item?.giftCard ? item?.giftCard?.balance : "--",
        ["Unit Price (CAD)"]: item?.giftCard ? "--" : item.price,
        ["Total Amount (CAD)"]: item?.giftCard ? "--" : item.price * item.qty,
        ["Order Total Amount (CAD)"]: item?.giftCard ? "--" : record.amount,
        ["From Email"]: record.from?.email || "--",
        ["From Username"]: `${record.from?.nickname || ""} ${record.from?.name || ""}`.trim() || "--",
        ["From Tel"]: record.from?.tel || "--",
        ["To Email"]: record.to?.email || "--",
        ["To Username"]: `${record.to?.nickname || ""} ${record.to?.name || ""}`.trim() || "--",
        ["To Tel"]: record.to?.tel || "--",
      });
    }
    return rows;
  };

  return user?.permission?.report != "all" ? (
    <Result
      status="403"
      title="403"
      subTitle="Sorry, you are not authorized to access this page."
      extra={
        <Button
          type="primary"
          onClick={() => {
            history.push("/login");
          }}
        >
          Back to Sign In
        </Button>
      }
    />
  ) : (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <SectionHeader
            title={
              <Title level={3} style={{ margin: 0 }}>
                Report
              </Title>
            }
          />
        </Col>
        <Col>
          <Space>
            <Button
              size="large"
              type="primary"
              onClick={() => {
                csvUtil.exportToCSV({
                  fileName: csvFileName,
                  csvData: filteredData.flatMap(getCSVRowData),
                });
              }}
            >
              Export CSV
            </Button>
            {/* <Button size="large" onClick={() => {}}>
              Filter
            </Button> */}
          </Space>
        </Col>
      </Row>

      <MainTabs />
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <ActionHeader
            pagination={pagination}
            pageSize={20}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            count={filteredDataCount}
            onActionClick={(value) => {
              log.debug(value);
            }}
          />
          <Table
            className="white-table-header"
            rowSelection={undefined}
            sticky
            loading={isFetchingLogs}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              current: pagination,
              pageSize: 20,
              position: ["none", "bottomCenter"],
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
            scroll={{ x: "100%" }} // Adjust the width as needed
            expandable={{ expandedRowRender, expandRowByClick: true }}
          />
        </Col>
      </Row>
      <Row style={{ height: "5rem" }}></Row>
    </div>
  );
};

export default Transfer;
